import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import SignatureCanvas from "react-signature-canvas";
import { riskobjectType, assessmentInitalKeys } from "../../../types/api";
import { Trash2, Printer, Download } from "lucide-react";
import {
  useRiskAssessment,
  usePremisesStore,
  useOrgUsersStore,
  useFireRiskProcessStore,
  useFireRiskSubProcessStore,
  useHazardousMaterialRiskProcessStore,
  useHazardousMaterialRiskSubProcessStore,
} from "../../../store/store";
import { filterObjectByKeys, isDefined } from "../../../common/utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface IRiskAssessmentProps {
  sendErrorMessage: (msg: string) => void;
  sendLoader: (loader: boolean) => void;
  sendSuccess: (status: boolean) => void;
  isReadOnly?: boolean;
  moduleName: string | undefined;
}
interface formHandle {
  submitRiskForm: () => void;
}
const SignatureForm = forwardRef<formHandle, IRiskAssessmentProps>(
  (
    { sendErrorMessage, sendLoader, sendSuccess, isReadOnly, moduleName },
    ref
  ) => {
    const {
      riskAssessment,
      loadingRisks,
      ras_error,
      updateRiskAssessment,
      asrScore,
      hwrScore,
      prevAssessmentSubmitDate,
      getSuccessMessage,
      getErrorMessage,
      fetchScore,
      AssetsTask,
      hwrList,
      asrList,
    } = useRiskAssessment();
    const { allPremises, fetchPremises } = usePremisesStore();
    const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
    const { fireRiskProcessessData, fetchFireRiskProcesses } =
      useFireRiskProcessStore();
    const { fireRiskSubProcessData, fetchFireRiskSubProcesses } =
      useFireRiskSubProcessStore();
    const {
      hazardousMaterialRiskProcessessData,
      fetchHazardousMaterialRiskProcesses,
    } = useHazardousMaterialRiskProcessStore();
    const {
      hazardousMaterialRiskSubProcessData,
      fetchHazardousMaterialRiskSubProcesses,
    } = useHazardousMaterialRiskSubProcessStore();
    const sigCanvasRef = useRef<SignatureCanvas | null>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [riskObject, setRiskObject] = useState<
      riskobjectType & { manufactureDate?: string; expireDate?: string }
    >({
      premises: [],
      responsiblePerson: [],
      assessor: [],
      assessorEmailAddress: [],
      addressLine1: "",
      ImageOfPremises: "",
      suggestedDate: "",
      signature: "",
    });
    const updateRiskObject = (
      event:
        | React.ChangeEvent<HTMLSelectElement>
        | React.ChangeEvent<HTMLInputElement>
    ) => {
      const local_riskObject = JSON.parse(JSON.stringify(riskObject));
      const { name, value } = event.target;
      local_riskObject[name] = value;
      setRiskObject(local_riskObject);
    };

    const formatDate = (dateStr?: string) => {
      const date = dateStr ? new Date(dateStr) : new Date();
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    const clearSignature = (): void => {
      sigCanvasRef.current?.clear();
    };

    useImperativeHandle(ref, () => ({
      submitRiskForm: async () => {
        if (riskObject.suggestedDate == "") {
          sendErrorMessage("Please provide a review date");
          return;
        }
        const local_riskObj = JSON.parse(JSON.stringify(riskObject));

        // --------- signature --------
        if (sigCanvasRef.current) {
          const dataURL = sigCanvasRef.current.toDataURL("image/png");
          local_riskObj.signature = dataURL;
          setRiskObject(local_riskObj);
        }
        if (local_riskObj.signature == "" || sigCanvasRef.current?.isEmpty()) {
          sendErrorMessage("Please provide a signature");
          return;
        }
        sendErrorMessage("");
        sendLoader(true);
        try {
          const filtered = filterObjectByKeys(
            local_riskObj,
            assessmentInitalKeys
          );
          filtered.status = "submitted";
          sendErrorMessage("");
          await updateRiskAssessment(filtered);
          const msg = getSuccessMessage();
          if (isDefined(msg)) {
            sendSuccess(true);
            await AssetsTask(filtered._id);
          }
          const erMsg = getErrorMessage();
          if (isDefined(erMsg)) {
            sendErrorMessage(erMsg);
          }
        } catch (err: any) {
          sendErrorMessage(err.message);
        } finally {
          sendLoader(false);
        }
      },
    }));

    const fetchScores = async () => {
      await fetchScore(riskAssessment?._id ?? "");
    };

    useEffect(() => {
      const local_assessment = JSON.parse(JSON.stringify(riskAssessment));
      setRiskObject(local_assessment);
      fetchScores();
      if (
        isDefined(riskAssessment.signature) &&
        sigCanvasRef.current !== null
      ) {
        sigCanvasRef.current.fromDataURL(riskAssessment.signature, {
          width: 300,
          height: 125,
        });
      }
    }, [riskAssessment]);

    useEffect(() => {
      if (!allPremises.length) fetchPremises();
      if (!orgUsersData.length) fetchOrgUsers();
      if (
        moduleName === "hazardous-materials" ||
        moduleName === "hazardousMaterials"
      ) {
        if (!hazardousMaterialRiskProcessessData.length)
          fetchHazardousMaterialRiskProcesses();
        if (!hazardousMaterialRiskSubProcessData.length)
          fetchHazardousMaterialRiskSubProcesses();
      } else {
        if (!fireRiskProcessessData.length) fetchFireRiskProcesses();
        if (!fireRiskSubProcessData.length) fetchFireRiskSubProcesses();
      }
    }, [moduleName]);

    const getProcessNameById = (id: string): string => {
      if (
        moduleName === "hazardous-materials" ||
        moduleName === "hazardousMaterials"
      ) {
        const found = (hazardousMaterialRiskProcessessData as any[]).find(
          (p: any) => p.id === id || p._id === id
        );
        if (found)
          return found.hazardousMaterialRiskProcessName || found.name || id;
      } else {
        const found = (fireRiskProcessessData as any[]).find(
          (p: any) => p.id === id || p._id === id
        );
        if (found) return found.fireRiskProcessName || found.name || id;
      }
      return id || "—";
    };

    const getSubProcessNameById = (id: string): string => {
      if (
        moduleName === "hazardous-materials" ||
        moduleName === "hazardousMaterials"
      ) {
        const found = (hazardousMaterialRiskSubProcessData as any[]).find(
          (sp: any) => sp.id === id || sp._id === id
        );
        if (found)
          return (
            found.hazardousMaterialRiskSubProcessName || found.name || id
          );
      } else {
        const found = (fireRiskSubProcessData as any[]).find(
          (sp: any) => sp.id === id || sp._id === id
        );
        if (found) return found.fireRiskSubProcessName || found.name || id;
      }
      return id || "—";
    };

    const getUserNameById = (id: string): string => {
      const user = orgUsersData.find((u: any) => u._id === id);
      return user ? `${user.firstName} ${user.lastName}` : id;
    };

    const getPremiseNameById = (id: string): string => {
      const premise = allPremises.find(
        (p: any) => p.id === id || p._id === id
      );
      return premise?.premisesName || id;
    };

    const hexToRgb = (hex: string): [number, number, number] => {
      const h = hex.replace("#", "");
      return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
      ];
    };

    const getScoreColor = (value: number | string): string => {
      if (moduleName === "external-emergency-preparedness") {
        if (value >= "0" && value <= "50") {
          return "#16a34a";
        } else if (value >= "51" && value <= "110") {
          return "#d97706";
        } else if (value >= "111") {
          return "#dc2626";
        }
      } else {
        if (value >= "0" && value <= "399") {
          return "#16a34a";
        } else if (value >= "400" && value <= "700") {
          return "#d97706";
        } else if (value >= "800") {
          return "#dc2626";
        }
      }
      return "#16202b";
    };

    const isSubmitted = riskAssessment?.status !== "open";

    const handlePrint = () => {
      window.print();
    };

    const buildRiskTableBody = (rows: any[]) =>
      rows.map((r: any) => {
        const p = Number(r.initialActualRisk?.ProbabilityOfFailure || 0);
        const s = Number(r.initialActualRisk?.SeverityOfOutCome || 0);
        const d = Number(r.initialActualRisk?.AbilityToDetectFailure || 0);
        const iv = p * s * d;
        const impl =
          r.recalculationOfRisk?.SolutionImplemented === "Yes fully" ||
          r.recalculationOfRisk?.SolutionImplemented === "Partially";
        const p2 = Number(r.recalculationOfRisk?.ProbabilityOfFailure || 0);
        const s2 = Number(r.recalculationOfRisk?.SeverityOfOutCome || 0);
        const d2 = Number(r.recalculationOfRisk?.AbilityToDetectFailure || 0);
        const rv = impl ? p2 * s2 * d2 : null;
        const ownerIds = Array.isArray(r.responsibilityIndividual)
          ? r.responsibilityIndividual
          : r.responsibilityIndividual
            ? [r.responsibilityIndividual]
            : [];
        const owners =
          ownerIds.map((id: string) => getUserNameById(id)).join(", ") ||
          "—";
        return [
          getProcessNameById(r.process),
          getSubProcessNameById(r.subProcesses),
          r.possibleFailureRisk || "—",
          p,
          s,
          d,
          iv,
          r.actionsSolution || "—",
          owners,
          r.TimeLineToImplement ? String(r.TimeLineToImplement) : "—",
          impl ? p2 : "—",
          impl ? s2 : "—",
          impl ? d2 : "—",
          rv != null ? rv : "—",
          impl ? "Mitigated" : "Open",
        ];
      });

    const addRiskTable = (
      doc: jsPDF,
      title: string,
      rows: any[],
      startY: number
    ): number => {
      doc.setFontSize(11);
      doc.setTextColor(20, 32, 43);
      doc.setFont("helvetica", "bold");
      doc.text(title, 12, startY);
      const body = buildRiskTableBody(rows);
      autoTable(doc, {
        startY: startY + 2,
        margin: { top: 16, left: 12, right: 12 },
        styles: {
          fontSize: 7,
          cellPadding: 1.6,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [14, 159, 142],
          textColor: 255,
          fontSize: 7,
          halign: "center",
        },
        columnStyles: {
          3: { halign: "center", cellWidth: 8 },
          4: { halign: "center", cellWidth: 8 },
          5: { halign: "center", cellWidth: 8 },
          6: { halign: "center", cellWidth: 11, fontStyle: "bold" },
          9: { halign: "center" },
          10: { halign: "center", cellWidth: 8 },
          11: { halign: "center", cellWidth: 8 },
          12: { halign: "center", cellWidth: 8 },
          13: { halign: "center", cellWidth: 11, fontStyle: "bold" },
        },
        head: [
          [
            { content: "Identify", colSpan: 3, styles: { halign: "left" } },
            { content: "Initial risk", colSpan: 4 },
            { content: "Mitigation", colSpan: 3, styles: { halign: "left" } },
            { content: "Residual risk", colSpan: 4 },
            { content: "Status", rowSpan: 2, styles: { valign: "middle" } },
          ],
          [
            "Process",
            "Sub-process",
            "Failure mode",
            "P",
            "S",
            "D",
            "RPN",
            "Action / solution",
            "Owner",
            "Days",
            "P",
            "S",
            "D",
            "RPN",
          ],
        ] as any,
        body: body.length
          ? body
          : [
            [
              {
                content: "No risks recorded",
                colSpan: 15,
                styles: { halign: "center", textColor: [123, 138, 151] },
              },
            ],
          ],
        didParseCell: (data: any) => {
          if (
            data.section === "body" &&
            (data.column.index === 6 || data.column.index === 13)
          ) {
            const v = parseInt(String(data.cell.text?.[0]), 10);
            if (!isNaN(v)) {
              const [r, g, b] = hexToRgb(getScoreColor(v));
              data.cell.styles.fillColor = [r, g, b];
              data.cell.styles.textColor = [255, 255, 255];
            }
          }
        },
      });
      return (doc as any).lastAutoTable.finalY;
    };

    const handleExportPdf = async () => {
      setIsExporting(true);
      try {
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        const premisesNames =
          (Array.isArray(riskAssessment?.premises)
            ? riskAssessment.premises
            : []
          )
            .map((id: string) => getPremiseNameById(id))
            .join(", ") || "—";
        const dutyHolder = Array.isArray(riskAssessment?.responsiblePerson)
          ? riskAssessment.responsiblePerson.join(", ") || "—"
          : (riskAssessment as any)?.responsiblePerson || "—";
        const assessorsNames =
          (Array.isArray(riskAssessment?.assessor)
            ? riskAssessment.assessor
            : []
          )
            .map((id: string) => getUserNameById(id))
            .join(", ") || "—";
        const assessorEmails = Array.isArray(
          riskAssessment?.assessorEmailAddress
        )
          ? riskAssessment.assessorEmailAddress.join(", ")
          : "";
        const address = riskAssessment?.addressLine1 || "—";
        const reviewDate = riskObject.suggestedDate
          ? formatDate(riskObject.suggestedDate)
          : "—";
        const currentDate = formatDate();

        const h = Number(hwrScore.HospitalWideRiskExistingScore || 0);
        const a = Number(asrScore.AreaSpecificRiskExistingScore || 0);
        const hPrev = Number(hwrScore.HospitalWideRiskPreviousScore || 0);
        const aPrev = Number(asrScore.AreaSpecificRiskPreviousScore || 0);

        const drawHeaderFooter = () => {
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.setFont("helvetica", "normal");
          doc.text(`Risk Assessment · ${premisesNames}`, 12, 8);
          doc.text(
            `Page ${(doc.internal as any).getCurrentPageInfo().pageNumber}`,
            pageW - 12,
            pageH - 6,
            { align: "right" }
          );
          if (!isSubmitted) {
            try {
              (doc as any).saveGraphicsState();
              (doc as any).setGState(
                new (doc as any).GState({ opacity: 0.1 })
              );
            } catch (e) { }
            doc.setTextColor(190, 30, 30);
            doc.setFontSize(90);
            doc.setFont("helvetica", "bold");
            doc.text("DRAFT", pageW / 2, pageH / 2, {
              align: "center",
              angle: 32,
            });
            try {
              (doc as any).restoreGraphicsState();
            } catch (e) { }
          }
        };

        doc.setTextColor(20, 32, 43);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text("Risk Assessment", 12, 20);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(90);
        doc.text(
          `Assessment date: ${currentDate}    Suggested review: ${reviewDate}`,
          12,
          26
        );

        let y = 34;
        const col2 = pageW / 2 + 6;
        const line = (label: string, val: string, x: number, yy: number) => {
          doc.setFontSize(8.5);
          doc.setTextColor(120);
          doc.setFont("helvetica", "bold");
          doc.text(label, x, yy);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(40);
          doc.text(doc.splitTextToSize(val || "—", pageW / 2 - 24), x, yy + 4);
        };
        line("Premises", premisesNames, 12, y);
        line("Responsible person / duty holder", dutyHolder, col2, y);
        y += 14;
        line("Address of premises", address, 12, y);
        line(
          "Assessors",
          `${assessorsNames}${assessorEmails ? "  (" + assessorEmails + ")" : ""}`,
          col2,
          y
        );
        y += 16;

        const hb = hexToRgb(getScoreColor(h));
        const ab = hexToRgb(getScoreColor(a));
        const hpb = hexToRgb(getScoreColor(hPrev));
        const apb = hexToRgb(getScoreColor(aPrev));
        doc.setDrawColor(227, 232, 236);
        doc.setFillColor(247, 249, 251);
        doc.roundedRect(12, y, pageW - 24, 16, 2, 2, "FD");
        doc.setFontSize(8.5);
        doc.setTextColor(90);
        doc.setFont("helvetica", "bold");
        doc.text("CURRENT SCORES", 16, y + 6);
        doc.text("PREVIOUS SCORES", pageW / 2 + 2, y + 6);
        doc.setFontSize(11);
        doc.setTextColor(hb[0], hb[1], hb[2]);
        doc.text(`HWR ${h}`, 16, y + 13);
        doc.setTextColor(ab[0], ab[1], ab[2]);
        doc.text(`AWR ${a}`, 44, y + 13);
        doc.setTextColor(hpb[0], hpb[1], hpb[2]);
        doc.text(`HWR ${hPrev}`, pageW / 2 + 2, y + 13);
        doc.setTextColor(apb[0], apb[1], apb[2]);
        doc.text(`AWR ${aPrev}`, pageW / 2 + 34, y + 13);
        y += 24;

        y = addRiskTable(doc, "Hospital wide risks", hwrList, y);
        y = addRiskTable(doc, "Area wide risks", asrList, y + 10);

        let sy = y + 12;
        if (sy > pageH - 46) {
          doc.addPage();
          sy = 24;
        }
        doc.setTextColor(20, 32, 43);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("Assessor signature", 12, sy);
        doc.setDrawColor(210);
        doc.rect(12, sy + 3, 70, 26);
        try {
          if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
            const img = sigCanvasRef.current.toDataURL("image/png");
            doc.addImage(img, "PNG", 13, sy + 4, 68, 24);
          }
        } catch (e) { }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(60);
        doc.text(assessorsNames || "—", 12, sy + 35);
        doc.text(`Date: ${currentDate}`, 12, sy + 40);

        const pageCount = (doc.internal as any).getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          drawHeaderFooter();
        }

        const fileSafeName = premisesNames.replace(/[^a-z0-9]+/gi, "-");
        doc.save(
          `Risk-Assessment-${fileSafeName || "Report"}${isSubmitted ? "" : "-DRAFT"
          }.pdf`
        );
      } finally {
        setIsExporting(false);
      }
    };

    return (
      <div className="px-6 py-5">
        <div className="flex justify-between items-start gap-4 flex-wrap mb-5">
          <p className="text-[15px] font-bold text-[#16202b] m-0">
            Overall risk report{" "}
            <span className="text-[#7b8a97] font-medium text-[13px]">
              (HWR — Hospital Wide Risk) &nbsp; (AWR — Area Wide Risk)
            </span>
          </p>
          <div className="flex items-center gap-2.5">
            <span
              className={`text-[11.5px] font-semibold px-2.5 py-1.5 rounded-full ${isSubmitted
                ? "text-[#16a34a] bg-[#e7f6ec]"
                : "text-[#d97706] bg-[#fdf1e0]"
                }`}
            >
              {isSubmitted ? "Submitted ✓" : "Draft — not yet submitted"}
            </span>
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold border border-[#e3e8ec] bg-white text-[#43525f] hover:bg-[#eef2f5] transition-colors"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={isExporting}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold border border-[#0e9f8e] bg-[#0e9f8e] text-white hover:bg-[#0b8577] transition-colors disabled:opacity-60"
            >
              <Download className="w-3.5 h-3.5" />{" "}
              {isExporting ? "Exporting..." : "Export PDF"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-5 items-start">
          <div className="border border-[#e3e8ec] rounded-xl p-5 text-center">
            <h3 className="text-base font-bold mb-1.5 text-[#1f6feb]">
              Current Scores
            </h3>
            <p className="text-xs text-[#43525f] mb-5">
              Assessment date: {formatDate()}
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <div
                  className="text-[34px] font-extrabold leading-none"
                  style={{
                    color: getScoreColor(
                      hwrScore.HospitalWideRiskExistingScore
                    ),
                  }}
                >
                  {hwrScore.HospitalWideRiskExistingScore}
                </div>
                <div className="text-xs text-[#43525f] mt-2.5">
                  HWR total risk score
                </div>
              </div>
              <div>
                <div
                  className="text-[34px] font-extrabold leading-none"
                  style={{
                    color: getScoreColor(
                      asrScore.AreaSpecificRiskExistingScore
                    ),
                  }}
                >
                  {asrScore.AreaSpecificRiskExistingScore}
                </div>
                <div className="text-xs text-[#43525f] mt-2.5">
                  AWR total risk score
                </div>
              </div>
            </div>
          </div>

          <div className="border border-[#e3e8ec] rounded-xl p-5 text-center">
            <h3 className="text-base font-bold mb-1.5 text-[#dc2626]">
              Previous Score
            </h3>
            <p className="text-xs text-[#43525f] mb-5">
              Assessment date: {formatDate(prevAssessmentSubmitDate)}
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <div
                  className="text-[34px] font-extrabold leading-none"
                  style={{
                    color: getScoreColor(
                      hwrScore.HospitalWideRiskPreviousScore
                    ),
                  }}
                >
                  {hwrScore.HospitalWideRiskPreviousScore}
                </div>
                <div className="text-xs text-[#43525f] mt-2.5">
                  HWR total risk score
                </div>
              </div>
              <div>
                <div
                  className="text-[34px] font-extrabold leading-none"
                  style={{
                    color: getScoreColor(
                      asrScore.AreaSpecificRiskPreviousScore
                    ),
                  }}
                >
                  {asrScore.AreaSpecificRiskPreviousScore}
                </div>
                <div className="text-xs text-[#43525f] mt-2.5">
                  AWR total risk score
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-4">
              <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
                Suggested date for review
              </label>
              <input
                type="date"
                className="w-full p-2.5 rounded-lg border border-[#e3e8ec] text-sm"
                value={riskObject.suggestedDate}
                name="suggestedDate"
                disabled={isReadOnly}
                onChange={(e) => updateRiskObject(e)}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-[13px] font-semibold text-[#16202b] m-0">
                  Signature
                </label>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={clearSignature}
                    title="Clear"
                    className="border-none bg-transparent text-[#dc2626] cursor-pointer p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {isReadOnly ? (
                <div className="w-full h-[150px] rounded-lg border border-[#e3e8ec] bg-white flex items-center justify-center overflow-hidden">
                  {riskObject.signature ? (
                    <img
                      src={riskObject.signature}
                      alt="Assessor signature"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-sm text-[#7b8a97]">
                      No signature available
                    </span>
                  )}
                </div>
              ) : (
                <div className="w-full h-[150px] rounded-lg border border-[#e3e8ec] bg-white">
                  <SignatureCanvas
                    penColor="black"
                    canvasProps={{
                      width: 400,
                      height: 150,
                      className: "w-full h-full",
                    }}
                    ref={sigCanvasRef}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default SignatureForm;
